import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import client from '@/api/client';
import { listFamilies } from '@/api/family';
import type { FamilyListItem } from '@/api/family';

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface AnbiyamSummary {
  anbiyamId:    number;
  anbiyamName:  string;
  zone:         number;
  totalFamilies: number;
  paidFamilies:  number;
}

interface FamilyDue {
  familyId:    number;
  familyCode:  string;
  headOfFamily: string;
  anbiyamId:   number;
  anbiyamName: string;
  zone:        number;
  monthsPaid:  number;
  monthsUnpaid: number;
}

interface MapData {
  year:             number;
  summary:          AnbiyamSummary[];
  familiesWithDues: FamilyDue[];
}

/* Zone aggregate (grouped from anbiyam-level data) */
interface ZoneGroup {
  zone:          number;
  totalFamilies: number;
  paidFamilies:  number;
  anbiyams:      AnbiyamSummary[];
}

/* ─── Zone colours — fixed for zones 1–8 ────────────────────────────────── */
const ZONE_COLORS = [
  '#185FA5', // Zone 1 — Blue
  '#534AB7', // Zone 2 — Violet
  '#A32D2D', // Zone 3 — Red
  '#0F6E56', // Zone 4 — Teal
  '#854F0B', // Zone 5 — Amber
  '#3B6D11', // Zone 6 — Green
  '#993556', // Zone 7 — Pink
  '#993C1D', // Zone 8 — Coral
];

function zoneColor(zone: number): string {
  return ZONE_COLORS[(zone - 1) % ZONE_COLORS.length] ?? '#6B7280';
}

/* ─── SVG polygon definitions (one per zone slot 1–8) ───────────────────── */
function bboxOf(path: string) {
  const nums = (path.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number);
  const xs = nums.filter((_, i) => i % 2 === 0);
  const ys = nums.filter((_, i) => i % 2 === 1);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

const ZONE_POLYGONS = [
  { path: 'M 0,0 L 171,0 L 184,140 L 0,133 Z',               cx: 82,  cy: 67  },
  { path: 'M 171,0 L 336,0 L 336,144 L 184,140 Z',           cx: 254, cy: 70  },
  { path: 'M 336,0 L 500,0 L 500,133 L 336,144 Z',           cx: 416, cy: 67  },
  { path: 'M 0,133 L 184,140 L 151,269 L 0,267 Z',           cx: 78,  cy: 200 },
  { path: 'M 184,140 L 336,144 L 313,264 L 151,269 Z',       cx: 246, cy: 204 },
  { path: 'M 336,144 L 500,133 L 500,267 L 313,264 Z',       cx: 414, cy: 200 },
  { path: 'M 0,267 L 151,269 L 313,264 L 257,400 L 0,400 Z', cx: 147, cy: 332 },
  { path: 'M 313,264 L 500,267 L 500,400 L 257,400 Z',       cx: 404, cy: 332 },
].map((p) => ({ ...p, bbox: bboxOf(p.path) }));

/* ─── Tooltip ────────────────────────────────────────────────────────────── */
interface TooltipProps { x: number; y: number; zg: ZoneGroup; }
function Tooltip({ x, y, zg }: TooltipProps) {
  const pct = zg.totalFamilies
    ? Math.round((zg.paidFamilies / zg.totalFamilies) * 100)
    : 0;
  return (
    <div
      className="pointer-events-none fixed z-50 bg-navy text-white text-xs font-georgia rounded shadow-lg px-3 py-2"
      style={{ left: x + 12, top: y - 10 }}
    >
      <p className="font-bold mb-0.5">Zone {zg.zone}</p>
      <p>{zg.paidFamilies}/{zg.totalFamilies} families paid — {pct}%</p>
      <p className="opacity-75">{zg.anbiyams.length} anbiyam{zg.anbiyams.length !== 1 ? 's' : ''}</p>
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────────────────── */
export default function AnbiyamMapPage() {
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();

  const currentYear = new Date().getFullYear();
  const [year,         setYear]         = useState(currentYear);
  const [data,         setData]         = useState<MapData | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [selectedZone, setSelectedZone] = useState<number | null>(null);
  const [hovered,      setHovered]      = useState<number | null>(null);
  const [tooltip,      setTooltip]      = useState<{ x: number; y: number; zg: ZoneGroup } | null>(null);

  /* families for selected zone's anbiyams */
  const [allFamilies,  setAllFamilies]  = useState<FamilyListItem[]>([]);
  const [loadingFams,  setLoadingFams]  = useState(false);
  const [familyFilter, setFamilyFilter] = useState<'all' | 'dues' | 'paid'>('all');
  /* selected anbiyam within the zone (optional drill-down) */
  const [selAnbiyam,   setSelAnbiyam]   = useState<number | null>(null);

  /* ── load map data ── */
  const load = useCallback(async (yr: number) => {
    setLoading(true);
    setError('');
    try {
      const r = await client.get<{ data: MapData }>(`/anbiyam/map-summary?year=${yr}`);
      setData(r.data.data);
    } catch {
      setError('Failed to load map data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  /* ── auto-select zone from URL param ── */
  useEffect(() => {
    if (!data) return;
    const zp = searchParams.get('zone');
    if (zp) setSelectedZone(parseInt(zp, 10));
  }, [data, searchParams]);

  /* ── group anbiyams into zones ── */
  const zones = useMemo<ZoneGroup[]>(() => {
    if (!data) return [];
    const map: Record<number, ZoneGroup> = {};
    data.summary.forEach((a) => {
      const z = a.zone || 0;
      if (!map[z]) map[z] = { zone: z, totalFamilies: 0, paidFamilies: 0, anbiyams: [] };
      map[z].totalFamilies += a.totalFamilies;
      map[z].paidFamilies  += a.paidFamilies;
      map[z].anbiyams.push(a);
    });
    return Object.values(map).sort((a, b) => a.zone - b.zone);
  }, [data]);

  const selectedZoneGroup = zones.find((z) => z.zone === selectedZone) ?? null;

  /* ── families with dues for the selected zone ── */
  const zoneDues = useMemo(() => {
    if (!data || !selectedZoneGroup) return [] as FamilyDue[];
    return data.familiesWithDues.filter((f) => f.zone === selectedZoneGroup.zone);
  }, [data, selectedZoneGroup]);

  const dueIds = useMemo(() => new Set(zoneDues.map((f) => f.familyId)), [zoneDues]);
  const dueDetail: Record<number, FamilyDue> = useMemo(() => {
    const m: Record<number, FamilyDue> = {};
    zoneDues.forEach((f) => { m[f.familyId] = f; });
    return m;
  }, [zoneDues]);

  /* ── fetch all families for selected anbiyam (within zone) ── */
  useEffect(() => {
    if (!selAnbiyam) { setAllFamilies([]); return; }
    let cancelled = false;
    setLoadingFams(true);
    setFamilyFilter('all');
    listFamilies({ anbiyamId: selAnbiyam, pageSize: 200 })
      .then((r) => { if (!cancelled) setAllFamilies(r.items); })
      .catch(() => { if (!cancelled) setAllFamilies([]); })
      .finally(() => { if (!cancelled) setLoadingFams(false); });
    return () => { cancelled = true; };
  }, [selAnbiyam]);

  const displayFamilies = allFamilies.filter((f) => {
    if (familyFilter === 'dues') return dueIds.has(f.familyId);
    if (familyFilter === 'paid') return !dueIds.has(f.familyId);
    return true;
  });

  /* ── handlers ── */
  function handleZoneClick(zone: number) {
    if (selectedZone === zone) { setSelectedZone(null); setSelAnbiyam(null); }
    else { setSelectedZone(zone); setSelAnbiyam(null); setAllFamilies([]); }
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-georgia text-2xl font-bold text-navy">Anbiyam Map</h1>
          <p className="font-georgia text-sm text-teal">
            8 zones · click a zone to explore anbiyams and families
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-georgia text-xs text-gray-500">Year</span>
          {Array.from({ length: currentYear - 2023 + 1 }, (_, i) => currentYear - i).map((yr) => (
            <button
              key={yr}
              onClick={() => { setYear(yr); setSelectedZone(null); setSelAnbiyam(null); }}
              className={`px-3 py-1 text-xs font-georgia rounded border transition-colors ${
                yr === year ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-300 hover:border-navy'
              }`}
            >{yr}</button>
          ))}
        </div>
      </div>

      {error && <p className="font-georgia text-sm text-red-600 mb-4">{error}</p>}

      <div className="flex flex-col lg:flex-row gap-4">

        {/* SVG Map */}
        <div className="flex-1 bg-white border border-grid-border p-4 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center h-80 text-teal font-georgia text-sm">Loading map…</div>
          ) : (
            <svg viewBox="0 0 500 400" className="w-full h-auto select-none" style={{ maxHeight: '460px' }}>
              {zones.map((zg) => {
                const poly      = ZONE_POLYGONS[(zg.zone - 1) % ZONE_POLYGONS.length];
                const base      = zoneColor(zg.zone);
                const isSelected = selectedZone === zg.zone;
                const isHovered  = hovered === zg.zone;
                const pct        = zg.totalFamilies
                  ? Math.round((zg.paidFamilies / zg.totalFamilies) * 100)
                  : 0;

                /* Progress fill: the zone shape is drawn in its own full color as
                   before; a dark translucent overlay covers the pending share (the
                   top 100-pct% of the shape's height), so the plain zone color still
                   showing at the bottom reads as "collected" and the shaded portion
                   above it reads as "pending" — no change to the zone's own hue. */
                const clipId     = `zoneClip-${zg.zone}`;
                const { minX, maxX, minY, maxY } = poly.bbox;
                const totalH     = maxY - minY;
                const collectedH = totalH * (pct / 100);
                const boundaryY  = maxY - collectedH;
                const fillOpacity = isSelected ? 1 : isHovered ? 0.80 : 0.60;

                return (
                  <g key={zg.zone}>
                    <defs>
                      <clipPath id={clipId}><path d={poly.path} /></clipPath>
                    </defs>
                    <path
                      d={poly.path}
                      fill={base}
                      fillOpacity={fillOpacity}
                      stroke={isSelected ? '#1B3A6B' : '#FFFFFF'}
                      strokeWidth={isSelected ? 2.5 : 1.5}
                      style={{ cursor: 'pointer', transition: 'fill-opacity 0.15s' }}
                      onMouseEnter={() => setHovered(zg.zone)}
                      onMouseMove={(e) => setTooltip({ x: e.clientX, y: e.clientY, zg })}
                      onMouseLeave={() => { setHovered(null); setTooltip(null); }}
                      onClick={() => handleZoneClick(zg.zone)}
                    />
                    {pct < 100 && (
                      <rect
                        x={minX} y={minY} width={maxX - minX} height={Math.max(0, boundaryY - minY)}
                        clipPath={`url(#${clipId})`} fill="#000000" fillOpacity={0.55}
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    {pct > 0 && pct < 100 && (
                      <line x1={minX} y1={boundaryY} x2={maxX} y2={boundaryY}
                        stroke="#FFFFFF" strokeWidth={1} strokeOpacity={0.55}
                        clipPath={`url(#${clipId})`} style={{ pointerEvents: 'none' }} />
                    )}
                    <text x={poly.cx} y={poly.cy - 9} textAnchor="middle" fontSize="12"
                      fontFamily="Georgia, serif" fill="#fff" fontWeight={isSelected ? '700' : '600'}
                      style={{ pointerEvents: 'none' }}>
                      Zone {zg.zone}
                    </text>
                    <text x={poly.cx} y={poly.cy + 6} textAnchor="middle" fontSize="10"
                      fontFamily="Georgia, serif" fill="rgba(255,255,255,0.85)"
                      style={{ pointerEvents: 'none' }}>
                      {zg.totalFamilies > 0 ? `${pct}%` : '—'}
                    </text>
                    <text x={poly.cx} y={poly.cy + 19} textAnchor="middle" fontSize="9"
                      fontFamily="Georgia, serif" fill="rgba(255,255,255,0.70)"
                      style={{ pointerEvents: 'none' }}>
                      {zg.anbiyams.length} anbiyam{zg.anbiyams.length !== 1 ? 's' : ''}
                    </text>
                  </g>
                );
              })}
              {zones.length === 0 && (
                <text x="250" y="200" textAnchor="middle" fontSize="14" fill="#9CA3AF" fontFamily="Georgia, serif">
                  No data for {year}
                </text>
              )}
            </svg>
          )}

          {/* Zone colour legend */}
          {!loading && (
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100">
              {zones.map((zg) => {
                const pct = zg.totalFamilies ? Math.round((zg.paidFamilies / zg.totalFamilies) * 100) : 0;
                const col = zoneColor(zg.zone);
                return (
                  <button key={zg.zone} onClick={() => handleZoneClick(zg.zone)}
                    className="flex items-center gap-1.5 hover:opacity-80">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: col }} />
                    <span className="font-georgia text-xs text-gray-600">Zone {zg.zone}</span>
                    <span className="font-georgia text-xs font-bold" style={{ color: col }}>{pct}%</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="w-full lg:w-80 flex-shrink-0">
          {!selectedZoneGroup ? (
            /* Zone overview list */
            <div className="bg-white border border-grid-border p-4">
              <p className="font-georgia text-sm font-bold text-navy mb-3">Zone Overview</p>
              {loading ? (
                <p className="font-georgia text-xs text-teal">Loading…</p>
              ) : (
                <div className="space-y-2">
                  {zones.map((zg) => {
                    const pct = zg.totalFamilies ? Math.round((zg.paidFamilies / zg.totalFamilies) * 100) : 0;
                    const col = zoneColor(zg.zone);
                    const barCol = pct >= 80 ? '#059669' : pct >= 50 ? '#D97706' : '#DC2626';
                    return (
                      <div key={zg.zone} onClick={() => handleZoneClick(zg.zone)}
                        className="cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: col }} />
                          <span className="font-georgia text-xs font-bold text-navy flex-1">Zone {zg.zone}</span>
                          <span className="font-georgia text-xs font-bold" style={{ color: col }}>{pct}%</span>
                        </div>
                        <div style={{ height: '3px', background: '#F3F4F6', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: barCol, borderRadius: '2px' }} />
                        </div>
                        <p className="font-georgia text-xs text-gray-400 mt-0.5">
                          {zg.totalFamilies} families · {zg.anbiyams.length} anbiyams
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="font-georgia text-xs text-gray-400 mt-4">Click a zone to drill down.</p>
            </div>
          ) : (() => {
            const col  = zoneColor(selectedZoneGroup.zone);
            const pct  = selectedZoneGroup.totalFamilies
              ? Math.round((selectedZoneGroup.paidFamilies / selectedZoneGroup.totalFamilies) * 100)
              : 0;
            const barCol = pct >= 80 ? '#059669' : pct >= 50 ? '#D97706' : '#DC2626';

            return (
              <div className="bg-white border border-grid-border overflow-hidden">
                {/* Zone header */}
                <div style={{ background: col, padding: '14px 16px' }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-georgia text-base font-bold text-white">Zone {selectedZoneGroup.zone}</p>
                      <p className="font-georgia text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
                        {selectedZoneGroup.anbiyams.length} anbiyam{selectedZoneGroup.anbiyams.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <button onClick={() => { setSelectedZone(null); setSelAnbiyam(null); }}
                      className="text-white opacity-70 hover:opacity-100 text-xl leading-none">×</button>
                  </div>
                  <div className="flex gap-5 mt-3">
                    {[
                      { label: 'Families', value: selectedZoneGroup.totalFamilies },
                      { label: 'Paid up',  value: selectedZoneGroup.paidFamilies  },
                      { label: 'Have dues', value: selectedZoneGroup.totalFamilies - selectedZoneGroup.paidFamilies },
                      { label: 'Rate',     value: `${pct}%`                        },
                    ].map(({ label, value }) => (
                      <div key={label} className="text-center">
                        <p className="font-georgia text-sm font-bold text-white">{value}</p>
                        <p className="font-georgia text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-white bg-opacity-30 overflow-hidden">
                    <div className="h-full rounded-full bg-white" style={{ width: `${pct}%`, opacity: 0.9 }} />
                  </div>
                </div>

                <div className="p-3 space-y-3">
                  {/* Anbiyams in this zone */}
                  <div>
                    <p className="font-georgia text-xs font-bold text-navy mb-2">Anbiyams in Zone {selectedZoneGroup.zone}</p>
                    <div className="space-y-1">
                      {selectedZoneGroup.anbiyams.map((a) => {
                        const aPct = a.totalFamilies ? Math.round((a.paidFamilies / a.totalFamilies) * 100) : 0;
                        const isActive = selAnbiyam === a.anbiyamId;
                        const duesInA = zoneDues.filter((d) => d.anbiyamId === a.anbiyamId).length;
                        return (
                          <div key={a.anbiyamId}
                            onClick={() => setSelAnbiyam(isActive ? null : a.anbiyamId)}
                            className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded cursor-pointer transition-colors"
                            style={{
                              background: isActive ? col + '22' : '#FAFAFA',
                              border: `0.5px solid ${isActive ? col : '#E5E7EB'}`,
                            }}>
                            <div className="min-w-0">
                              <p className="font-georgia text-xs font-bold text-navy truncate">{a.anbiyamName}</p>
                              <p className="font-georgia text-xs text-gray-400">
                                {a.totalFamilies} families{duesInA > 0 ? ` · ${duesInA} dues` : ''}
                              </p>
                            </div>
                            <span className="font-georgia text-xs font-bold flex-shrink-0" style={{ color: barCol }}>
                              {aPct}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Family list for selected anbiyam */}
                  {selAnbiyam && (() => {
                    const selA = selectedZoneGroup.anbiyams.find((a) => a.anbiyamId === selAnbiyam);
                    const aDues = zoneDues.filter((d) => d.anbiyamId === selAnbiyam);
                    const aDueIds = new Set(aDues.map((d) => d.familyId));
                    const shown = displayFamilies;
                    const paidCount = allFamilies.filter((f) => !aDueIds.has(f.familyId)).length;

                    return (
                      <div className="border-t border-gray-100 pt-3">
                        <p className="font-georgia text-xs font-bold text-navy mb-2">
                          {selA?.anbiyamName} families
                        </p>
                        {/* filter tabs */}
                        <div className="flex gap-1.5 mb-2">
                          {([
                            { key: 'all',  label: `All (${allFamilies.length})` },
                            { key: 'dues', label: `Dues (${aDues.length})` },
                            { key: 'paid', label: `Paid (${paidCount})` },
                          ] as const).map(({ key, label }) => (
                            <button key={key} onClick={() => setFamilyFilter(key)}
                              className={`flex-1 font-georgia text-xs py-1 rounded border transition-colors ${
                                familyFilter === key ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-200'
                              }`}
                              style={familyFilter === key ? { background: col, borderColor: col } : {}}>
                              {label}
                            </button>
                          ))}
                        </div>
                        {loadingFams ? (
                          <p className="font-georgia text-xs text-teal text-center py-3">Loading…</p>
                        ) : (
                          <div className="space-y-1">
                            {shown.map((f) => {
                              const hasDue = aDueIds.has(f.familyId);
                              const due    = dueDetail[f.familyId];
                              return (
                                <div key={f.familyId} onClick={() => navigate(`/families/${f.familyId}`)}
                                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-blue-50 transition-colors"
                                  style={{ border: `0.5px solid ${hasDue ? '#FECACA' : '#E5E7EB'}`, background: hasDue ? '#FFF7F7' : '#FAFAFA' }}>
                                  <div className="min-w-0">
                                    <p className="font-georgia text-xs font-bold text-navy truncate">{f.familyHead || f.familyCode}</p>
                                    <p className="font-georgia text-xs text-gray-400">{f.familyCode}</p>
                                  </div>
                                  <div className="flex-shrink-0 text-right">
                                    {hasDue ? (
                                      <>
                                        <span className="font-georgia text-xs font-bold text-red-600 block">{due?.monthsUnpaid ?? '?'} mo due</span>
                                        <span className="font-georgia text-xs text-gray-400">{due?.monthsPaid ?? '?'} paid</span>
                                      </>
                                    ) : (
                                      <span className="font-georgia text-xs font-bold text-green-700">✓ paid</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Dues families (if no anbiyam selected) */}
                  {!selAnbiyam && zoneDues.length > 0 && (
                    <div className="border-t border-gray-100 pt-3">
                      <p className="font-georgia text-xs font-bold text-navy mb-2">
                        {zoneDues.length} famil{zoneDues.length === 1 ? 'y' : 'ies'} with dues in this zone
                      </p>
                      <div className="space-y-1">
                        {zoneDues.map((f) => (
                          <div key={f.familyId} onClick={() => navigate(`/families/${f.familyId}`)}
                            className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded cursor-pointer hover:bg-red-50 transition-colors"
                            style={{ border: '0.5px solid #FECACA', background: '#FFF7F7' }}>
                            <div className="min-w-0">
                              <p className="font-georgia text-xs font-bold text-navy truncate">{f.headOfFamily || f.familyCode}</p>
                              <p className="font-georgia text-xs text-gray-400">{f.familyCode} · {f.anbiyamName}</p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <span className="font-georgia text-xs font-bold text-red-600 block">{f.monthsUnpaid} mo due</span>
                              <span className="font-georgia text-xs text-gray-400">{f.monthsPaid} paid</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!selAnbiyam && zoneDues.length === 0 && (
                    <p className="font-georgia text-xs text-green-700 font-bold text-center pt-1">
                      ✓ All families in this zone are up to date
                    </p>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {tooltip && <Tooltip x={tooltip.x} y={tooltip.y} zg={tooltip.zg} />}
    </div>
  );
}
